import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sparkles, X, Send, Bot, User, TrendingUp, TrendingDown,
  DollarSign, AlertTriangle, Lightbulb, ChevronRight, Eye, RefreshCw
} from 'lucide-react';
import FormulaReveal, { HOTEL_FORMULAS } from '@/components/ui/FormulaReveal';

const API_URL = import.meta.env.VITE_BACKEND_URL;

const suggestedQuestions = [
  { text: 'What\'s my competitive position today?', icon: TrendingUp },
  { text: 'Show rate parity violations', icon: AlertTriangle },
  { text: 'Recommend pricing strategy', icon: Lightbulb },
  { text: 'Analyze market demand trends', icon: DollarSign },
];

const initialMessages = [
  {
    role: 'assistant',
    content: 'Hello! I\'m Prowen AI, your intelligent hotel revenue management assistant. I can help you analyze your hotel\'s performance, identify pricing opportunities, and provide strategic recommendations powered by Prowen AI. What would you like to know?',
    timestamp: new Date()
  }
];

export default function AIAssistant({ isOpen, onClose }) {
  const [messages, setMessages] = useState(initialMessages);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  // Generate a new session ID when the component opens
  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    }
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  const sendMessageToAPI = async (message, context = {}) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Please log in to use the AI Assistant');
    }

    const response = await axios.post(
      `${API_URL}/api/ai/chat/`,
      {
        message,
        session_id: sessionId,
        context
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  };

  const handleSend = async (text = input) => {
    if (!text.trim() || isTyping) return;

    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    setError(null);

    try {
      // Build context from available hotel data
      const context = {
        hotel_name: 'Hotel Turmeric',
        current_rate: 3143,
        occupancy: 72,
        market_demand: 68,
        rate_parity_status: '8 violations detected'
      };

      const data = await sendMessageToAPI(text, context);
      
      // Update session ID if returned by API
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to get response';
      setError(errorMessage);
      
      // Add error message to chat
      const errorMsg = {
        role: 'assistant',
        content: `I apologize, but I encountered an issue: ${errorMessage}. Please try again.`,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleNewChat = () => {
    setMessages(initialMessages);
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-card border-l border-border shadow-xl z-[100] flex flex-col" data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-accent/10">
            <Sparkles className="h-4 w-4 text-accent" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Prowen AI</h3>
            <p className="text-xs text-muted-foreground">Powered by Claude Sonnet</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleNewChat} 
            className="h-8 w-8"
            title="New conversation"
            data-testid="new-chat-btn"
          >
            <RefreshCw className="h-4 w-4 text-muted-foreground hover:text-primary" />
          </Button>
          <FormulaReveal
            {...HOTEL_FORMULAS.AI_INSIGHT_GENERATION}
            title="AI Assistant Engine"
            description="The AI Assistant uses Claude Sonnet 4.5 to analyze hotel data and provide contextual insights."
          >
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Eye className="h-4 w-4 text-muted-foreground hover:text-primary" />
            </Button>
          </FormulaReveal>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8" data-testid="close-ai-assistant-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              data-testid={`chat-message-${message.role}-${index}`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.role === 'assistant' 
                  ? message.isError ? 'bg-destructive/10' : 'bg-accent/10' 
                  : 'bg-primary/10'
              }`}>
                {message.role === 'assistant' ? (
                  <Bot className={`h-4 w-4 ${message.isError ? 'text-destructive' : 'text-accent'}`} />
                ) : (
                  <User className="h-4 w-4 text-primary" />
                )}
              </div>
              <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
                <div className={`inline-block p-3 rounded-xl text-sm ${
                  message.role === 'assistant' 
                    ? message.isError 
                      ? 'bg-destructive/10 text-destructive-foreground border border-destructive/20'
                      : 'bg-muted text-foreground' 
                    : 'bg-primary text-primary-foreground'
                }`}>
                  <div className="whitespace-pre-wrap">{message.content}</div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex gap-3" data-testid="ai-typing-indicator">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-accent" />
              </div>
              <div className="bg-muted p-3 rounded-xl">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Suggested Questions */}
      {messages.length <= 2 && (
        <div className="p-3 border-t border-border">
          <p className="text-xs text-muted-foreground mb-2">Suggested questions:</p>
          <div className="space-y-1">
            {suggestedQuestions.map((q, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs h-8 gap-2"
                onClick={() => handleSend(q.text)}
                disabled={isTyping}
                data-testid={`suggested-question-${index}`}
              >
                <q.icon className="h-3 w-3 text-muted-foreground" />
                {q.text}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-border">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your analytics..."
            className="flex-1 text-sm"
            disabled={isTyping}
            data-testid="ai-chat-input"
          />
          <Button 
            size="icon" 
            onClick={() => handleSend()} 
            disabled={!input.trim() || isTyping}
            data-testid="ai-send-btn"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
